
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, Clock, MapPin, Car, Camera, SearchX, Users, BarChart3 } from 'lucide-react';
import { fetchHistoricoCompleto } from '@/lib/authFetch';

interface HistoricoItem {
  id: string;
  tipo: string;
  target: string;
  info: number;
  data_hora: string;
  location: string;
  categoria: string;
  camera_id?: number;
  modelo?: string;
  cor?: string;
  ano?: number;
}

interface HistoricoResponse {
  atividades: HistoricoItem[];
  pagina: number;
  limite: number;
  total: number;
  total_paginas: number;
}

const InvestigationHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);

  // Buscar histórico do backend
  const fetchHistorico = async (tipo?: string, paginaAtual = 1) => {
    setLoading(true);
    try {
      const params: { pagina: number; limite: number; tipo?: string } = { pagina: paginaAtual, limite:20 };
      if (tipo && tipo !== 'all') {
        params.tipo = tipo;
      }
      
      const response = await fetchHistoricoCompleto(params);
      setHistorico(response.atividades);
      setTotal(response.total);
      setTotalPaginas(response.total_paginas);
      setPagina(response.pagina);
    } catch {
      setHistorico([]);
      setTotal(0);
      setTotalPaginas(0);
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    fetchHistorico();
  }, []);

  // Filtrar por tipo
  const handleFilterChange = (newFilterType: string) => {
    setFilterType(newFilterType);
    setPagina(1);
    fetchHistorico(newFilterType, 1);
  };

  // Buscar por termo
  const handleSearch = () => {
    // Por enquanto, a busca é feita no frontend
    // Pode ser expandida para usar parâmetros de busca no backend
    setPagina(1);
    fetchHistorico(filterType, 1);
  };

  // Paginação
  const handlePageChange = (novaPagina: number) => {
    setPagina(novaPagina);
    fetchHistorico(filterType, novaPagina);
  };

  // Filtrar resultados por termo de busca (frontend)
  const filteredHistorico = historico.filter(item => {
    if (!searchTerm) return true;
    return (
      item.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tipo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.location.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const getStatusBadge = (categoria: string) => {
    switch (categoria) {
      case 'consulta':
        return <Badge className="bg-blue-500/20 text-blue-400">Consulta</Badge>;
      case 'deteccao':
        return <Badge className="bg-green-500/20 text-green-400">Detecção</Badge>;
      case 'facial':
        return <Badge className="bg-purple-500/20 text-purple-400">Facial</Badge>;
      case 'relatorio':
        return <Badge className="bg-orange-500/20 text-orange-400">Relatório</Badge>;
      case 'investigacao':
        return <Badge className="bg-yellow-500/20 text-yellow-400">Investigação</Badge>;
      case 'alerta':
        return <Badge className="bg-red-500/20 text-red-400">Alerta</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">Outro</Badge>;
    }
  };

  const getTypeIcon = (categoria: string) => {
    switch (categoria) {
      case 'consulta':
      case 'deteccao':
        return <Car className="w-4 h-4" />;
      case 'facial':
      case 'investigacao':
        return <Users className="w-4 h-4" />;
      case 'relatorio':
        return <BarChart3 className="w-4 h-4" />;
      case 'alerta':
        return <Search className="w-4 h-4" />;
      default:
        return <Search className="w-4 h-4" />;
    }
  };

  const formatDateTime = (dataHora: string) => {
    try {
      const date = new Date(dataHora);
      return {
        date: date.toLocaleDateString('pt-BR'),
        time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      };
    } catch {
      return { date: '--/--/----', time: '--:--' };
    }
  };

  // Calcular estatísticas
  const totalInvestigações = total;
  const mediaResultados = historico.length > 0 ? (historico.length / historico.length).toFixed(1) : '0.0';
  const taxaSucesso = historico.length > 0 ? Math.round((historico.filter(h => h.categoria !== 'alerta').length / historico.length) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-protec-gold" />
            Histórico de Investigações
          </CardTitle>
          <CardDescription className="text-gray-300">
            Consulte e visualize todas suas pesquisas realizadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 max-w-md">
            <Input
              placeholder="Buscar por placa ou ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-protec-dark border-protec-gold/30 text-white"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button 
              className="bg-protec-gold text-black hover:shadow-gold px-6"
              onClick={handleSearch}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Investigation Results */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Search className="w-5 h-5 text-protec-gold" />
            Resultados ({loading ? '...' : filteredHistorico.length})
          </CardTitle>
          <CardDescription className="text-gray-300">
            Histórico organizado por data mais recente
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-400">Carregando histórico...</div>
            </div>
          ) : filteredHistorico.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <SearchX className="w-16 h-16 text-gray-500 mb-4" />
              <p className="text-gray-400 text-lg">Nenhum resultado encontrado</p>
              <p className="text-gray-500 text-sm">
                {searchTerm 
                  ? `Não há detecções para "${searchTerm}"` 
                  : 'Realize buscas de placas para ver o histórico aqui.'}
              </p>
            </div>
          ) : (
            <>
          <div className="space-y-4">
                {filteredHistorico.map((item) => {
                  const { date, time } = formatDateTime(item.data_hora);
                  return (
                    <div key={item.id} className="bg-protec-dark rounded-lg p-4 border border-protec-gold/10">
                  <div className="grid md:grid-cols-3 gap-4">
                    {/* Image */}
                    <div className="relative">
                      <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                        <Camera className="w-8 h-8 text-gray-600" />
                        <span className="text-xs text-gray-600 ml-2">Imagem da Câmera</span>
                      </div>
                      <Badge className="absolute top-2 right-2 bg-protec-gold text-black text-xs">
                        95% Match
                      </Badge>
                    </div>
                    {/* Details */}
                    <div className="md:col-span-2 space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-white">
                          {item.target}
                        </h3>
                        <Badge variant="outline" className="border-green-500 text-green-400">
                          Detectado
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Modelo</p>
                          <p className="text-white">{item.modelo || "Não cadastrado"}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Cor</p>
                          <p className="text-white">{item.cor || "Não cadastrada"}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Ano</p>
                          <p className="text-white">{item.ano || "Não cadastrado"}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-protec-gold" />
                          <span className="text-gray-400">Última detecção:</span>
                          <span className="text-white">{date}, {time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-protec-gold" />
                          <span className="text-gray-400">Local:</span>
                          <span className="text-white">{item.location}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Camera className="w-4 h-4 text-protec-gold" />
                          <span className="text-gray-400">Câmera:</span>
                          <span className="text-white">CAM-{item.camera_id || item.info}</span>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
                  );
                })}
              </div>

              {/* Paginação */}
              {totalPaginas > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagina - 1)}
                    disabled={pagina <= 1}
                    className="border-protec-gold text-protec-gold hover:bg-protec-gold hover:text-black"
                  >
                    Anterior
                  </Button>
                  <span className="text-gray-400">
                    Página {pagina} de {totalPaginas}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagina + 1)}
                    disabled={pagina >= totalPaginas}
                    className="border-protec-gold text-protec-gold hover:bg-protec-gold hover:text-black"
                  >
                    Próxima
                  </Button>
          </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default InvestigationHistory;
